import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import './index.css'
import { router } from './router'
import { USE_REAL_API, installApiAuth } from './lib/api'
import { runMockBootstrap } from './lib/bootstrapFlow'
import { hydrateOrgContext } from './store/orgContext'
import { startAppearanceRuntime } from './store/appearance'
import { BootstrapError } from './components/loading/BootstrapLoader'
import { BootLoaderHandoff } from './components/loading/BootLoaderHandoff'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Don't retry client errors (4xx) — a 403/404 is deterministic, retrying
      // just makes the UI spin for seconds before showing the error. Transient
      // (network / 5xx) failures still retry.
      retry: (failureCount, error) => {
        const msg = error instanceof Error ? error.message : ''
        if (/\b4\d\d\b/.test(msg)) return false
        return failureCount < 2
      },
    },
  },
})

const SW_URL = `${import.meta.env.BASE_URL}mockServiceWorker.js`
const SW_SCOPE = import.meta.env.BASE_URL
const appRoot = createRoot(document.getElementById('root')!)

// Hard reset any stale MSW service worker registrations whose URL or scope no
// longer matches the current deploy. Without this a previously-cached worker
// (different integrity checksum / different scope after subpath change) stays
// installed but never receives MOCK_ACTIVATE — fetch handler then bypasses
// every request, nginx returns the SPA index.html as JSON fallback, and the
// UI reports "Liste yüklenemedi." This issue had surfaced before (M0-26 fix
// addressed URL prefixing only; this addresses worker lifecycle).
async function unregisterStaleWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    const absoluteSwUrl = new URL(SW_URL, window.location.origin).href
    const absoluteScope = new URL(SW_SCOPE, window.location.origin).href
    for (const reg of registrations) {
      const sw = reg.active || reg.waiting || reg.installing
      const scriptUrl = sw?.scriptURL ?? ''
      const scope = reg.scope ?? ''
      if (scriptUrl !== absoluteSwUrl || scope !== absoluteScope) {
        await reg.unregister()
      }
    }
  } catch {
    // Best-effort cleanup; ignore failures.
  }
}

async function enableMocks() {
  // Maket mode only (no VITE_API_BASE: dev, tests, the /m0/ simulation deploy).
  // Production runs against the real API and never calls this, so the MSW
  // worker and every src/mocks module stay a lazy chunk that real-API builds
  // never download (OPT-20261031-05).
  await unregisterStaleWorkers()
  const { worker } = await import('./mocks/browser')
  if (import.meta.env.DEV && !USE_REAL_API && import.meta.env.VITE_PROBLEM_REPORTING_PREVIEW === 'true') {
    const { setProblemReportingEnabled } = await import('./mocks/data/problem-reports/service')
    setProblemReportingEnabled(true)
  }
  await worker.start({
    onUnhandledRequest: 'warn',
    waitUntilReady: true,
    serviceWorker: { url: SW_URL },
  })
  installApiAuth()
}

function renderApp() {
  appRoot.render(
    <StrictMode>
      <BootLoaderHandoff />
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  )
}

function renderStartupError() {
  console.error('PEXIFY mock startup failed')
  appRoot.render(
    <StrictMode>
      <BootLoaderHandoff />
      <BootstrapError />
    </StrictMode>,
  )
}

// In real-engine mode any MSW service worker left registered from a prior maket
// session must be removed, else it can keep intercepting requests.
//
// unregister() only stops the worker from controlling FUTURE loads — a worker
// already controlling THIS page keeps intercepting /api/v1 (serving the bundled
// MSW mocks) for the rest of the session. So when a controller is present we
// unregister AND force a single guarded reload; the next load is uncontrolled
// and reaches the real backend. The sessionStorage flag prevents a reload loop.
const SW_RELOAD_FLAG = 'pexify-sw-disabled-reload'

async function disableMocks(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    if (registrations.length === 0) return false
    await Promise.all(registrations.map((reg) => reg.unregister()))
    if (
      navigator.serviceWorker.controller &&
      !sessionStorage.getItem(SW_RELOAD_FLAG)
    ) {
      sessionStorage.setItem(SW_RELOAD_FLAG, '1')
      window.location.reload()
      return true
    }
  } catch {
    // Best-effort cleanup; ignore failures.
  }
  return false
}

// Real-engine mode (VITE_API_BASE set) skips the maket/MSW layer and talks to
// the real backend. Default (no env var) keeps the simulation mocks on.
const stopAppearanceRuntime = startAppearanceRuntime()
import.meta.hot?.dispose(stopAppearanceRuntime)
hydrateOrgContext()

if (USE_REAL_API) {
  installApiAuth()
  disableMocks().then((reloading) => {
    if (!reloading) renderApp()
  })
} else {
  void runMockBootstrap(enableMocks, renderApp, renderStartupError)
}
